import { Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { finalize, forkJoin } from 'rxjs';
import { LocalizationService } from '../../../core/localization/localization.service';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  FormSchemaDiffItem,
  computeFormSchemaDiff,
  parseFormSchema
} from '../../../shared/dynamic-form/form-schema.models';
import { DynamicApiService } from '../dynamic-api.service';

export interface FormVersionOption {
  id: string;
  version: number;
  isPublished: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-form-version-diff',
  imports: [
    FormsModule,
    ButtonModule,
    SelectModule,
    TagModule,
    InfoHintComponent,
    AinStateComponent
  ],
  template: `
    <section class="version-diff">
      <ain-info-hint
        [titleKey]="'dynamic.versionDiff.infoTitle'"
        [descriptionKey]="'dynamic.versionDiff.infoDescription'"
        [bulletKeys]="['dynamic.versionDiff.infoBullet']" />

      <div class="picker-row">
        <label>{{ t('dynamic.versionDiff.baseVersion') }}</label>
        <p-select
          [options]="versionOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="baseVersionId()"
          appendTo="body"
          (ngModelChange)="baseVersionId.set($event)" />

        <label>{{ t('dynamic.versionDiff.compareVersion') }}</label>
        <p-select
          [options]="versionOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="compareVersionId()"
          appendTo="body"
          (ngModelChange)="compareVersionId.set($event)" />

        <button
          pButton
          type="button"
          [label]="t('dynamic.versionDiff.compare')"
          [disabled]="!canCompare() || loading()"
          (click)="compare()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('dynamic.versionDiff.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('dynamic.versionDiff.error')" [message]="error() ?? ''" />
      } @else if (diffItems().length) {
        <ul class="diff-list">
          @for (item of diffItems(); track $index) {
            <li class="diff-item">
              <p-tag [severity]="severityFor(item.changeType)" [value]="changeLabel(item.changeType)" />
              <span class="category">{{ t(item.categoryKey) }}</span>
              <strong>{{ item.label }}</strong>
              @if (item.details) {
                <small>{{ item.details }}</small>
              }
            </li>
          }
        </ul>
      } @else if (compared()) {
        <ain-state type="empty" [title]="t('dynamic.versionDiff.noChanges')" />
      }
    </section>
  `,
  styles: [`
    .version-diff { display: grid; gap: 12px; }
    .picker-row {
      display: grid;
      grid-template-columns: auto 1fr auto 1fr auto;
      gap: 8px;
      align-items: center;
    }
    @media (max-width: 900px) {
      .picker-row { grid-template-columns: 1fr; }
    }
    .diff-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 8px;
    }
    .diff-item {
      display: grid;
      gap: 4px;
      padding: 10px 12px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
    }
    .category { color: #64748b; font-size: 0.8rem; }
    .diff-item small { color: #475569; }
  `]
})
export class FormVersionDiffComponent {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly formId = input.required<string>();
  readonly versions = input<FormVersionOption[]>([]);

  readonly baseVersionId = signal<string | null>(null);
  readonly compareVersionId = signal<string | null>(null);
  readonly diffItems = signal<FormSchemaDiffItem[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly compared = signal(false);

  readonly versionOptions = computed(() =>
    this.versions().map(item => ({
      label: `v${item.version}${item.isPublished ? ' ★' : ''}`,
      value: item.id
    }))
  );

  canCompare(): boolean {
    const base = this.baseVersionId();
    const compare = this.compareVersionId();
    return !!base && !!compare && base !== compare;
  }

  compare(): void {
    const formId = this.formId();
    const baseId = this.baseVersionId();
    const compareId = this.compareVersionId();
    if (!formId || !baseId || !compareId || baseId === compareId) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.compared.set(false);

    forkJoin({
      base: this.api.getFormVersion(formId, baseId),
      compare: this.api.getFormVersion(formId, compareId)
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: ({ base, compare }) => {
          const culture = this.localization.activeCulture();
          const before = parseFormSchema(base.schemaSnapshot, culture);
          const after = parseFormSchema(compare.schemaSnapshot, culture);
          if (!before || !after) {
            this.error.set(this.t('dynamic.versionDiff.parseError'));
            return;
          }
          this.diffItems.set(computeFormSchemaDiff(before, after, culture));
          this.compared.set(true);
        },
        error: () => this.error.set(this.t('dynamic.versionDiff.error'))
      });
  }

  severityFor(changeType: FormSchemaDiffItem['changeType']): 'success' | 'danger' | 'warn' {
    if (changeType === 'added') return 'success';
    if (changeType === 'removed') return 'danger';
    return 'warn';
  }

  changeLabel(changeType: FormSchemaDiffItem['changeType']): string {
    switch (changeType) {
      case 'added':
        return this.t('dynamic.versionDiff.badge.added');
      case 'removed':
        return this.t('dynamic.versionDiff.badge.removed');
      default:
        return this.t('dynamic.versionDiff.badge.changed');
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
